import styled from 'styled-components';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import DatePicker, { DateRangeInput } from 'components/inputs/DatePicker';
import LabelWrap from 'components/inputs/LabelWrap';
import ContextMenu from 'components/ContextMenu';
import { useMemo } from 'react';
import Button from 'components/inputs/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { GroupingLevel } from 'api/model';
import MultiSelect, { Option } from 'components/inputs/MultiSelect';
import { useSupplierGetSuppliers } from 'api/supplier/supplier';
import useZipCodeAreas from 'hooks/useZipCodeAreas';

const ActionsWrapper = styled.div`
  padding-top: 10px;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: end;
  padding: 10px;
  gap: 10px;
`;

const DateSelectWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: end;
  gap: 5px;
  margin-left: 5px;
  white-space: nowrap;
  button {
    padding: 0 10px;
    height: 30px;
  }
`;

interface DateRangeOption {
  label: string;
  range: [Date, Date];
}

const groupingOptions: Option<GroupingLevel>[] = [
  { label: 'Dag', value: GroupingLevel.Day },
  { label: 'Vecka', value: GroupingLevel.Week },
  { label: 'Månad', value: GroupingLevel.Month },
  { label: 'År', value: GroupingLevel.Year },
];

const KPIsFilterBar = () => {
  const {
    dashboardState: {
      daterange,
      selectedSupplierIDs,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      groupingLevel,
    },
    setSelectedDateRange,
    setSelectedSupplierIDs,
    setSelectedFromCountyIDs,
    setSelectedToCountyIDs,
    setGroupingLevel,
  } = useDashboardControls();
  const { data: suppliers } = useSupplierGetSuppliers();
  const { selectableZipCodeAreas } = useZipCodeAreas();

  const supplierOptions = useMemo(() => {
    if (!suppliers) return [];
    return suppliers
      .filter((s) => s.isActive)
      .map((s) => ({
        label: s.name,
        value: s.id,
      }));
  }, [suppliers]);

  const countyOptions = useMemo(() => {
    return selectableZipCodeAreas.map((zca) => ({
      label: zca.areaName,
      value: zca.id,
    }));
  }, [selectableZipCodeAreas]);

  const dateRangeOptions: DateRangeOption[] = useMemo(
    () => [
      {
        label: 'Idag',
        range: [
          new Date(new Date().setHours(0, 0, 0, 0)),
          new Date(new Date().setHours(23, 59, 59, 999)),
        ],
      },
      {
        label: 'Senaste 7 dagar',
        range: [
          new Date(new Date().getTime() - 6 * 24 * 60 * 60 * 1000),
          new Date(),
        ],
      },
      {
        label: 'Innevarande månad',
        range: [
          new Date(new Date().getFullYear(), new Date().getMonth(), 1),
          new Date(
            new Date().getFullYear(),
            new Date().getMonth() + 1,
            0,
            23,
            59,
            59,
            999
          ),
        ],
      },
      {
        label: 'Förra månaden',
        range: [
          new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
          new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            0,
            23,
            59,
            59,
            999
          ),
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <ActionsWrapper>
      <Actions>
        <DateSelectWrapper>
          <LabelWrap label="Datum">
            <DatePicker
              calendarStartDay={1}
              customInput={
                <DateRangeInput daterange={[daterange[0], daterange[1]]} />
              }
              selectsRange
              startDate={daterange[0]}
              endDate={daterange[1]}
              dateFormat="yyyy-MM-dd"
              locale="sv"
              onChange={setSelectedDateRange}
              populateHolidays
              shouldCloseOnSelect
              showYearDropdown
              showMonthDropdown
              showWeekNumbers
            />
          </LabelWrap>
          <ContextMenu
            options={dateRangeOptions.map((ro) => ({
              label: ro.label,
              range: ro.range,
              onClick: () => setSelectedDateRange(ro.range),
            }))}
          >
            <Button>
              {dateRangeOptions.find(
                (ro) =>
                  daterange[0] &&
                  daterange[1] &&
                  daterange[0].getTime() === ro.range[0].getTime() &&
                  daterange[1].getTime() === ro.range[1].getTime()
              )?.label ?? 'Frival'}{' '}
              <FontAwesomeIcon icon={faAngleDown} />
            </Button>
          </ContextMenu>
        </DateSelectWrapper>
        <LabelWrap label="Gruppering">
          <ContextMenu
            options={groupingOptions.map((opt) => ({
              label: opt.label,
              onClick: () => setGroupingLevel(opt.value),
            }))}
          >
            <Button>
              {groupingOptions.find((opt) => opt.value === groupingLevel)
                ?.label ?? 'Dag'}{' '}
              <FontAwesomeIcon icon={faAngleDown} />
            </Button>
          </ContextMenu>
        </LabelWrap>
        <LabelWrap label="Leverantör">
          <MultiSelect
            placeholder="Välj leverantörer"
            onChange={(newVal) => {
              setSelectedSupplierIDs(Array.from(newVal));
            }}
            options={supplierOptions}
            value={new Set(selectedSupplierIDs)}
          />
        </LabelWrap>
        <LabelWrap label="Från-län">
          <MultiSelect
            placeholder="Välj län"
            onChange={(newVal) => {
              setSelectedFromCountyIDs(Array.from(newVal));
            }}
            options={countyOptions}
            value={new Set(selectedFromCountyIDs)}
          />
        </LabelWrap>
        <LabelWrap label="Till-län">
          <MultiSelect
            placeholder="Välj län"
            onChange={(newVal) => {
              setSelectedToCountyIDs(Array.from(newVal));
            }}
            options={countyOptions}
            value={new Set(selectedToCountyIDs)}
          />
        </LabelWrap>
      </Actions>
    </ActionsWrapper>
  );
};

export default KPIsFilterBar;
